import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useAxiosHttpClient } from '@/modules/shared/infra/axios-http-client.infra'
import { HttpStatusCode } from '@/modules/shared/protocols/http-client.protocol'

import {
	customerFormJsonAddService,
	customerFormJsonEditOrViewService,
} from '../services/client-form-json.service'
import {
	type TCustomerFormLoadCityService,
	customerFormLoadCityService,
} from '../services/client-form-load-city.service'
import {
	type TCustomerFormLoadCnpjService,
	customerFormLoadCnpjaService,
	customerFormLoadCnpjService,
} from '../services/client-form-load-cnpj.service'
import {
	type TCustomerFormLoadZipCodeService,
	customerFormLoadZipCodeService,
} from '../services/client-form-load-zip-code.service'
import { customerFormSaveActionService } from '../services/client-form-save-action.service'
import { customerFormTablesService } from '../services/client-form-tables.service'
import {
	type TCustomerFormValidCnpjOrCpfService,
	customerFormValidCnpjOrCpfService,
} from '../services/client-form-valid-cnpj-cpf.service'
import {
	useCustomerFormStore,
	useCustomerFormStoreAction,
} from '../stores/customer-form.store'
import type { TCustomerFormJson } from '../types/client-form-json.type'
import type { TCity } from '../types/client-form-promise-city.type'
import type { TCustomerFormCnpj } from '../types/client-form-promise-cnpj.type'
import type { TCustomerFormZipCode } from '../types/client-form-promise-zip-code.type'
import { useSyncRequest } from '@/modules/shared/hooks/use-sync-request.hook'

export const useCustomerFormData = () => {
	const navigate = useNavigate()

	const {
		customerFormJson,
		customerFormTable,
		operationType,
		clientSelected,
		cnpjOfClientToDataSearch,
		isSaveForm,
	} = useCustomerFormStore()

	const {
		setIsSaveForm,
		setCustomerFormCnpj,
		setCustomerFormJson,
		setCustomerFormTable,
		setCustomerFormCities,
		setCustomerFormZipCode,
		setOperationToFillValues,
		setIsFillValuesAfterRequestCities,
	} = useCustomerFormStoreAction

	const { id } = useParams()

	const { axiosRequest: requestCustomerFormTablesParams } = useAxiosHttpClient({
		sendRequest: customerFormTablesService,
	})

	const { axiosRequest: requestCustomerFormJsonAdd } = useAxiosHttpClient({
		sendRequest: customerFormJsonAddService,
	})

	const { axiosRequest: requestCustomerFormJsonEditOrView } =
		useAxiosHttpClient({
			sendRequest: customerFormJsonEditOrViewService,
		})

	const { axiosRequest: requestCustomerFormValidCnpjOrCpf } =
		useAxiosHttpClient({
			sendRequest: customerFormValidCnpjOrCpfService,
		})

	const { axiosRequest: requestCustomerFormSaveAction } = useAxiosHttpClient({
		sendRequest: customerFormSaveActionService,
	})

	const requestCustomerFormCnpj = useMutation<
		TCustomerFormCnpj,
		unknown,
		TCustomerFormLoadCnpjService
	>({
		mutationFn: async (data) => {
			try {
				return await customerFormLoadCnpjService({
					payload: data.payload,
				})
			} catch (error) {

				return customerFormLoadCnpjaService({
					payload: data.payload,
				})
			}
		},
		onError: (error) => {
			toast.info(
				'Não foi possível localizar informações deste CNPJ.',
			)
			console.error('Error loading CNPJ data:', error)
		},
		retry: 0,
	})

	const requestCustomerFormCities = useMutation<
		TCity[],
		unknown,
		TCustomerFormLoadCityService
	>({
		mutationFn: async (data) =>
			customerFormLoadCityService({
				payload: data.payload,
			}),
	})

	const requestCustomerFormZipCode = useMutation<
		TCustomerFormZipCode,
		unknown,
		TCustomerFormLoadZipCodeService
	>({
		mutationFn: async (data) =>
			customerFormLoadZipCodeService({
				payload: data.payload,
			}),
		onError: (error) => {
			toast.error("Dados não encontrados para o CEP informado")
			console.error('Error loading zip code data:', error)
		},
	})

	const submitCustomerFormTablesParams = () => {
		requestCustomerFormTablesParams.mutate({})
	}

	const submitCustomerFormValidCnpjOrCpf = async ({
		payload,
	}: TCustomerFormValidCnpjOrCpfService) => {
		await requestCustomerFormValidCnpjOrCpf.mutateAsync({
			payload,
			showInfo: true,
		})
	}

	const submitCustomerFormCnpj = ({
		payload,
	}: TCustomerFormLoadCnpjService) => {
		requestCustomerFormCnpj.mutate({ payload })
	}

	const submitCustomerFormCities = (ufId: string) => {
		requestCustomerFormCities.mutate({ payload: { ufId } })
	}

	const submitCustomerFormZipCode = async ({
		payload,
	}: TCustomerFormLoadZipCodeService) => {
		await requestCustomerFormZipCode.mutateAsync({ payload })
	}

	const { syncRequest } = useSyncRequest()

	const submitCustomerFormSaveAction = async () => {
		const newPayload = {
			operationType: 'I',
			customerFormJson,
		}

		if (operationType !== 'add') {
			newPayload.operationType = 'A'
		}

		await requestCustomerFormSaveAction.mutateAsync({
			payload: newPayload,
			showInfo: true,
			showSuccess: true,
		})

		syncRequest(['-1']);


	}

	const queryClient = useQueryClient()

	useEffect(() => {
		if (requestCustomerFormTablesParams.isSuccess) {
			if (requestCustomerFormTablesParams.data.status === HttpStatusCode.OK) {
				setCustomerFormTable(
					JSON.parse(requestCustomerFormTablesParams.data.body),
				)
				setOperationToFillValues('customerForm')

				if (operationType === 'add' && !id) {
					const cached = queryClient.getQueryData<TCustomerFormJson>([
						'customerForm',
						'new',
					])

					if (!cached) {
						return requestCustomerFormJsonAdd.mutate({})
					}

					setCustomerFormJson(cached)
					submitCustomerFormCities(String(customerFormTable?.listaUF[0]?.idUf))

					return
				}

				requestCustomerFormJsonEditOrView.mutate({
					payload: {
						clientId: clientSelected?.idEntidade || id,
					},
				})
			}
		}

		// eslint-disable-next-line
	}, [requestCustomerFormTablesParams.isLoading])

	useEffect(() => {
		if (requestCustomerFormJsonAdd.isSuccess) {
			if (requestCustomerFormJsonAdd.data.status === HttpStatusCode.OK) {
				const responseRequest = JSON.parse(requestCustomerFormJsonAdd.data.body)

				setCustomerFormJson(responseRequest)
				submitCustomerFormCities(String(customerFormTable?.listaUF[0]?.idUf))
			}
		}

		// eslint-disable-next-line
	}, [requestCustomerFormJsonAdd.isLoading])

	useEffect(() => {
		if (requestCustomerFormJsonEditOrView.isSuccess) {
			if (requestCustomerFormJsonEditOrView.data.status === HttpStatusCode.OK) {
				const responseRequest: TCustomerFormJson = JSON.parse(
					requestCustomerFormJsonEditOrView.data.body,
				)

				setCustomerFormJson(responseRequest)
				submitCustomerFormCities(responseRequest?.fkUF)
			}
		}

		// eslint-disable-next-line
	}, [requestCustomerFormJsonEditOrView.isLoading])

	useEffect(() => {
		if (requestCustomerFormCities.isSuccess) {
			setCustomerFormCities(requestCustomerFormCities.data)
			setIsFillValuesAfterRequestCities(true)
		}

		// eslint-disable-next-line
	}, [requestCustomerFormCities.isLoading])

	useEffect(() => {
		if (requestCustomerFormValidCnpjOrCpf.isSuccess) {
			if (requestCustomerFormValidCnpjOrCpf.data.status === HttpStatusCode.OK) {
				if (isSaveForm) {
					submitCustomerFormSaveAction()
					setIsSaveForm(false)
					return
				}

				if (cnpjOfClientToDataSearch?.length !== 14) {
					return
				}

				setOperationToFillValues('clientCnpj')
				submitCustomerFormCnpj({
					payload: {
						cnpj: cnpjOfClientToDataSearch,
					},
				})
				return
			}

			if (isSaveForm && operationType !== 'add') {
				submitCustomerFormSaveAction()
				setIsSaveForm(false)
			}
		}

		// eslint-disable-next-line
	}, [requestCustomerFormValidCnpjOrCpf.isLoading])

	useEffect(() => {
		if (requestCustomerFormCnpj.isSuccess) {
			setCustomerFormCnpj(requestCustomerFormCnpj.data)

			const stateId = requestCustomerFormCnpj.data?.codigo_municipio_ibge
				.toString()
				.slice(0, 2)
			submitCustomerFormCities(stateId)
		}

		// eslint-disable-next-line
	}, [requestCustomerFormCnpj.isLoading])

	useEffect(() => {
		if (requestCustomerFormZipCode.isSuccess) {
			setCustomerFormZipCode(requestCustomerFormZipCode.data)
			submitCustomerFormCities(
				requestCustomerFormZipCode.data?.ibge.toString().slice(0, 2),
			)
		}

		// eslint-disable-next-line
	}, [requestCustomerFormZipCode.isLoading])

	useEffect(() => {
		if (requestCustomerFormSaveAction.isSuccess) {
			if (requestCustomerFormSaveAction.data.status === HttpStatusCode.OK) {
				navigate('/clientes')
			}
		}

		// eslint-disable-next-line
	}, [requestCustomerFormSaveAction.isLoading])

	return {
		isLoadingCustomerFormData:
			requestCustomerFormTablesParams.isLoading ||
			requestCustomerFormJsonAdd.isLoading ||
			requestCustomerFormJsonEditOrView.isLoading,
		action: {
			onSubmitCustomerFormCnpj: submitCustomerFormCnpj,
			onSubmitCustomerFormCities: submitCustomerFormCities,
			onSubmitCustomerFormZipCode: submitCustomerFormZipCode,
			onSubmitCustomerFormSaveAction: submitCustomerFormSaveAction,
			onSubmitCustomerFormTablesParams: submitCustomerFormTablesParams,
			onSubmitCustomerFormValidCnpjOrCpf: submitCustomerFormValidCnpjOrCpf,
		},
	}
}
